import React, { useState } from 'react';
import {
  Box, Typography, MenuItem, Select, Tabs, Tab, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const BRANDS = ['Fischer', 'Salomon', 'Atomic', 'Rossignol', 'Madshus'];
  
const fischerFAData = [
    { weight: 25, Skin: '13–17', Classic: '13–15', Klister: '10–16', Skate: '28–33' },
    { weight: 30, Skin: '19–23', Classic: '18–21', Klister: '10–23', Skate: '39–46' },
    { weight: 35, Skin: '20–27', Classic: '20–24', Klister: '12–24', Skate: '44–51' },
    { weight: 40, Skin: '21–30', Classic: '22–27', Klister: '16–27', Skate: '50–56' },
    { weight: 45, Skin: '24–32', Classic: '25–30', Klister: '20–29', Skate: '55–61' },
    { weight: 50, Skin: '27–34', Classic: '27–33', Klister: '23–31', Skate: '61–66' },
    { weight: 55, Skin: '29–37', Classic: '29–35', Klister: '27–34', Skate: '66–72' },
    { weight: 60, Skin: '30–40', Classic: '30–36', Klister: '30–36', Skate: '72–78' },
    { weight: 65, Skin: '32–44', Classic: '32–39', Klister: '33–39', Skate: '77–83' },
    { weight: 70, Skin: '34–45', Classic: '35–42', Klister: '36–41', Skate: '83–88' },
    { weight: 75, Skin: '36–47', Classic: '36–45', Klister: '39–44', Skate: '88–94' },
    { weight: 80, Skin: '38–50', Classic: '38–48', Klister: '42–47', Skate: '94–100' },
    { weight: 85, Skin: '42–52', Classic: '40–51', Klister: '45–50', Skate: '99–105' },
    { weight: 90, Skin: '45–55', Classic: '43–54', Klister: '48–52', Skate: '105–111' },
    { weight: 95, Skin: '48–57', Classic: '45–56', Klister: '51–54', Skate: '110–117' },
    { weight: 100, Skin: '48–60', Classic: '48–60', Klister: '54–57', Skate: '116–123' },
    { weight: 105, Skin: '53–67', Classic: '50–64', Klister: '57–60', Skate: '121–130' },
    { weight: 110, Skin: '56–70', Classic: '55–66', Klister: '60–63', Skate: '126–143' },
  ];
  
  function FAChartInteractive() {
    const [selectedType, setSelectedType] = useState('Skate');
  
    const getSplitValues = (rangeStr) => {
      const [min, max] = rangeStr.split('–').map(v => parseInt(v));
      const mid = Math.round((min + max) / 2);
      return [min, mid, max];
    };
  
    return (
      <Box>
        <Typography variant="subtitle1">FA Value Chart (Stiffness Recommendation)</Typography>
        <Typography sx={{ fontSize: 14, mb: 1 }}>
          Select ski type to view recommended FA ranges by skier weight.
        </Typography>
        <Typography sx={{ fontSize: 13, mb: 2, fontStyle: 'italic' }}>
          • Beginner value: beginner / recreational / average glide / safe kick<br />
          • Experienced value: experienced / average skier / good glide / good kick<br />
          • Fast skier value: good technique / fast skier / fast glide / supportive kick
        </Typography>
  
        <ToggleButtonGroup
          value={selectedType}
          exclusive
          onChange={(e, val) => val && setSelectedType(val)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="Skin">Skin</ToggleButton>
          <ToggleButton value="Classic">Classic</ToggleButton>
          <ToggleButton value="Klister">Klister</ToggleButton>
          <ToggleButton value="Skate">Skate</ToggleButton>
        </ToggleButtonGroup>
  
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Bodyweight (kg)</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Beginner (FA)</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Experienced (FA)</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Fast Skier (FA)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fischerFAData.map(row => {
                const [low, mid, high] = getSplitValues(row[selectedType]);
                return (
                  <TableRow key={row.weight}>
                    <TableCell>{row.weight}</TableCell>
                    <TableCell align="center">{low}</TableCell>
                    <TableCell align="center">{mid}</TableCell>
                    <TableCell align="center">{high}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

function FischerBaseGrindTable() {
  const theme = useTheme();

  const rows = [
    // [code, use, temp, category]
    ['C1-1', 'Very cold', '-10°C and colder', 'cold'],
    ['C12-1', 'Cold artificial snow', '-7°C and colder', 'cold'],
    ['C3-1', 'Cold fine snow (new snow)', '-5°C to -13°C', 'cold'],
    ['C8-1', 'Universal cold', '-5°C to -15°C', 'cold'],
    ['P10-1', 'Floury snow', '0°C to -10°C', 'cold'],
    ['TZ1-1', 'Universal', '0°C to -10°C', 'universal'],
    ['P1-1', 'New Snow', '+3°C to -5°C', 'universal'],
    ['P5-1', 'Standard plus', '+5°C to -10°C', 'universal'],
    ['P5-0', 'Universal in all snow conditions', '+2°C to -7°C', 'universal'],
    ['P10-3', 'Grained snow and wet new snow', '0°C to -7°C', 'universal'],
    ['P2-1', 'Fine snow', '+1°C to -8°C', 'universal'],
    ['P10-13', 'Similar to P10-3', '+2°C to -7°C', 'universal'],
    ['P10-2', 'Cold New Snow', '-2°C to -10°C', 'universal'],
    ['P11-1', 'Coarse grained, frozen transformed snow', '+5°C to -5°C', 'warm'],
    ['P11-2', 'Coarse grained to wet snow', '0°C and warmer', 'warm'],
    ['P9-2', 'Wet conditions', '0°C and warmer', 'warm'],
    ['P14-1', 'Wet, sticky snow, rain', '0°C and warmer', 'warm'],
  ];

  const getRowColor = (type) => {
    switch (type) {
      case 'cold': return theme.palette.info.main + '22';       // soft blue
      case 'universal': return theme.palette.warning.main + '22'; // soft amber
      case 'warm': return theme.palette.error.main + '22';       // soft red
      default: return 'transparent';
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Grind</TableCell>
            <TableCell>Use</TableCell>
            <TableCell>Temp</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(([code, desc, temp, type]) => (
            <TableRow key={code} sx={{ backgroundColor: getRowColor(type) }}>
              <TableCell>{code}</TableCell>
              <TableCell>{desc}</TableCell>
              <TableCell>{temp}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RossignolBaseGrindTable() {
    const theme = useTheme();
  
    const rows = [
        // [code, temp, snow, used_for, conditions, category]
        ['A1', '-30°C to -10°C',  'Natural and Artificial', 'SK / CL',  'Cold and dry conditions', 'universal'],
        ['RC5', '-15°C to -3°C',  'Natural and Artificial', 'SK / CL',  'Cold and Uni cold snow, works both on S1 and S2', 'universal'],
        ['RU2', '-10°C to -3°C',  'Natural & Artificial Snow', 'SK / CL',  'Uni cold small grain', 'cold'],
        ['RU18', '-12°C to -3°C',  'Uni Cold', 'SK / CL',  'Same as A6B', 'cold'],
        ['A6B', '-12°C to -3°C',  'Uni Cold', 'SK / CL',  '', 'cold'],
        ['RU15', '-5°C to 0°C',  'Artifical snow', 'SK / CL',  'Artificial snow, sugar snow, uni conditions', 'cold'],
        ['RUW6', '-5°C to 0°C',  'Uni/Uni wet', 'SK / CL',  'Any type of snow with a wet to humid start', 'warm'],
        ['RUW1', '-3°C to 0°C',  'Uni/Uni wet', 'SK / CL',  'Uni. Uni wet, Falling snow, glazy snow', 'warm'],
        ['RUW10', '-5°C to 0°C',  'Uni wet', 'SK / CL',  'Universal wet with all type of grain, falling snow 0°C', 'warm'],
        ['A11', '-3°C to 0°C',  'Uni wet', 'SK / CL',  '', 'warm'],
        ['RW1', '0°C',  'Wet snow', 'SK',  'Wet snow conditions, very versatile depending on grain.', 'warm'],
        ['A36', '0°C',  'Wet snow', 'SK',  'Wet snow', 'warm'],
        ['RW10', '0°C',  'Wet snow', 'SK / CL',  'Wet snow conditions, very versatile depending on grain.', 'warm'],
        ['A30', '0°C',  'Wet snow', 'CL',  'Wet snow only CL', 'warm'],
        ['RW692', '0°C',  'Wet snow', 'CL', 'Very wet, coarse-grained snow', 'warm'],
    ];
  
    const getRowColor = (type) => {
      switch (type) {
        case 'cold': return theme.palette.info.main + '22';       // soft blue
        case 'universal': return theme.palette.warning.main + '22'; // soft amber
        case 'warm': return theme.palette.error.main + '22';       // soft red
        default: return 'transparent';
      }
    };
  
    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Grind</TableCell>
              <TableCell>Temp Range</TableCell>
              <TableCell>Snow Quality</TableCell>
              <TableCell>For</TableCell>
              <TableCell>Conditions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(([code, temp, snow, used_for, conditions, category]) => (
              <TableRow key={code} sx={{ backgroundColor: getRowColor(category) }}>
                <TableCell>{code}</TableCell>
                <TableCell>{temp}</TableCell>
                <TableCell>{snow}</TableCell>
                <TableCell>{used_for}</TableCell>
                <TableCell>{conditions}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }


const fischerData = {
  tabs: ['Ski Molds', 'Base Grinds', 'FA Stiffness'],
  content: [
    <Box key="molds">
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Ski Molds/Constructions</Typography>
      <Typography sx={{ fontSize: 14, mb: 1 }}>
          This is the mold the ski is actually made in. This construction effects how the ski feels and performs. 
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Skating Molds</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>Code</TableCell><TableCell>Description</TableCell></TableRow>
          </TableHead>
          <TableBody>
            <TableRow><TableCell>1Q</TableCell><TableCell>1Q is the standard skate ski construction using the 610 construction. 1Q has a universal range meeting all snow types, conditions and is responsive to all skating lechmeues used by skiers.</TableCell></TableRow>
            <TableRow><TableCell>1QG</TableCell><TableCell>The 1QG utilizes 610 construction with a softer shovel and front part of the ski.</TableCell></TableRow>
            <TableRow><TableCell>11</TableCell><TableCell>The 11 is built utilizing the 611 construction which is identified by a lighter core version and a slightly higher resting camber (distance between the ski itself without load and the flat surface on which it rests) compared to the 1Q. The 11 has a universal usage range meeting all snow types and conditions.</TableCell></TableRow>
            <TableRow><TableCell>11G</TableCell><TableCell>The 11G utilizes 611 construction with a softer shovel and front part of the ski combined with a different core.</TableCell></TableRow>
            <TableRow><TableCell>Z0</TableCell><TableCell>The ZO has a higher resting camber (distance between the ski itself without load and the flat surface on which it rests) compared to the 1Q with the curve more centered close to the midpoint of the ski. The ZO construction works best in compact snow conditions and skiers who ski in a more upright position.</TableCell></TableRow>
            <TableRow><TableCell>DK</TableCell><TableCell>The DK is built with 610 construction but utilizes the lightest core material produced and lighter core laminate materials.</TableCell></TableRow>
            <TableRow><TableCell>DP (Sprint)</TableCell><TableCell>The DP (Sprint) is built with 610 construction but has a low resting camber (distance between the ski itself without load and the flat surface on which it rests). DP is built for double poling without kick wax.</TableCell></TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Classic Molds</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>Code</TableCell><TableCell>Description</TableCell></TableRow>
          </TableHead>
          <TableBody>
            <TableRow><TableCell>9Q2</TableCell><TableCell>The 9Q2 is identified with short contact areas in the tip and tail. The 9Q2 is for violet hard wax to klister snow conditions.</TableCell></TableRow>
            <TableRow><TableCell>9BT</TableCell><TableCell>The 9BT is similar to the 9Q2 with the exception of having a stronger reinforcement laminate layer which makes it ideal for use in colder, new snow conditions.</TableCell></TableRow>
            <TableRow><TableCell>9FU</TableCell><TableCell>The 9FU is similar to the 9BT but utilizes a different reinforcement laminate layer compared to 9BT. This new laminate gives the ski a very dynamic ski feeling and is primarily used in klister conditions.</TableCell></TableRow>
            <TableRow><TableCell>8Q2</TableCell><TableCell>The 8Q2 is identified with long contact areas in the tip and tail. The 8Q2 is for cold hard wax snow conditions.</TableCell></TableRow>
            <TableRow><TableCell>81L</TableCell><TableCell>The 81L is built utilizing the 812 construction but has a much longer wax pocket.</TableCell></TableRow>
            <TableRow><TableCell>90L</TableCell><TableCell>The 90L is similar to the 9Q2 but has a much longer wax pocket.</TableCell></TableRow>
            <TableRow><TableCell>DP</TableCell><TableCell>The DP is built using similar characteristics to the 8Q2 but has more stiffness similar to a skating ski and has a much lower height of the resting camber (distance between the ski itself without load and the flat surface on which it rests) compared to 8Q2. DP is built for double poling without kick wax.</TableCell></TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>,
    <Box key="base-grinds">
      <Typography variant="subtitle1">Base Grind Chart (Temp Ranges)</Typography>
      <FischerBaseGrindTable />

    </Box>,
    <FAChartInteractive key="stiffness" />
  ]
};

const rossignolData = {
    tabs: ['Ski Constructions', 'Base Grinds', 'Ski Lengths'],
    content: [
      <Box key="rossi-molds">
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Rossignol Ski Constructions</Typography>
        <Typography sx={{ fontSize: 14, mb: 2 }}>
          These molds represent Rossignol's World Cup ski designs, each suited for specific snow conditions.
        </Typography>
        {/* Classic skis */}
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Classic Ski Constructions</Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Construction</TableCell>
                <TableCell>Design Name</TableCell>
                <TableCell>Conditions</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Base</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                {Construction: 'I02', Design: 'C1', Conditions: 'Cold', Notes: 'Hard wax', Base: 'Cold (0)'},
                {Construction: 'I92', Design: 'C2', Conditions: 'Uni Cold', Notes: 'Hard wax / Klister cover', Base: 'Uni(9)'},
                {Construction: 'H93', Design: 'C2', Conditions: 'Uni / Uni Wet', Notes: 'Klister cover / Universal', Base: 'Uni(9)'}, 
                {Construction: 'H143', Design: 'C3', Conditions: 'Wet', Notes: 'Klister', Base: 'Red(7)'},
                {Construction: 'I151', Design: 'C1', Conditions: 'Falling snow', Notes: 'No wax', Base: 'Uni with insert'}
              ].map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{row.Construction}</TableCell>
                  <TableCell>{row.Design}</TableCell>
                  <TableCell>{row.Conditions}</TableCell>
                  <TableCell>{row.Notes}</TableCell>
                  <TableCell>{row.Base}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Skate Skis */}
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Skate Ski Constructions</Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Construction</TableCell>
                <TableCell>Design</TableCell>
                <TableCell>From</TableCell>
                <TableCell>Base</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                {Construction: 'D01', Design: 'S1', From: 'FRA', Base: 'Cold(0)', Notes: ''},
                {Construction: 'D01', Design: 'S1', From: 'SPAIN', Base: 'Cold(0)', Notes: ''},
                {Construction: 'F93', Design: 'SX', From: 'FRA', Base: 'Uni(9)', Notes: 'Short camber / Big openings'},
                {Construction: 'F93', Design: 'SX', From: 'SPAIN', Base: 'Uni(9)', Notes: 'Short camber / Big openings'},
                {Construction: 'D92', Design: 'S2', From: 'SPAIN', Base: 'Uni(9)', Notes: ''},
                {Construction: 'F73', Design: 'S3', From: 'FRA', Base: 'Red(7)', Notes: 'Short camber / Big openings'},
                {Construction: 'F73', Design: 'S3', From: 'SPAIN', Base: 'Red(7)', Notes: 'Short camber / Big openings'},
              ].map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{row.Construction}</TableCell>
                  <TableCell>{row.Design}</TableCell>
                  <TableCell>{row.From}</TableCell>
                  <TableCell>{row.Base}</TableCell>
                  <TableCell>{row.Notes}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>,
  
      <Box key="rossi-grinds">
        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Rossignol World Cup Base Grinds (2025–2026)</Typography>
        <Typography sx={{ fontSize: 14, mb: 2 }}>
          This chart outlines grind options and their optimal snow and temperature ranges.
        </Typography>
        <RossignolBaseGrindTable/>
      </Box>,

<Box key="ski-lengths">
  <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Rossignol Ski Lengths</Typography>

  <Typography variant="h6" sx={{ mt: 2 }}>Classic</Typography>
  <TableContainer component={Paper} sx={{ mb: 3 }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Length (cm)</TableCell>
          <TableCell>Camber Test Location</TableCell>
          <TableCell>Full Body Weight</TableCell>
          <TableCell>Half Body Weight</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[
          { length: 197, camber: 'minus 12 cm of Balance point', full: '60 kg', half: '30 kg' },
          { length: 202, camber: 'minus 14 cm of Balance point', full: '70 kg', half: '35 kg' },
          { length: 207, camber: 'minus 14 cm of Balance point', full: '80 kg', half: '40 kg' },
        ].map((row) => (
          <TableRow key={row.length}>
            <TableCell>{row.length}</TableCell>
            <TableCell>{row.camber}</TableCell>
            <TableCell>{row.full}</TableCell>
            <TableCell>{row.half}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>

  <Typography variant="h6" sx={{ mt: 2 }}>Skate</Typography>
  <TableContainer component={Paper}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Length (cm)</TableCell>
          <TableCell>Camber Location</TableCell>
          <TableCell>Full Body Weight</TableCell>
          <TableCell>Half Body Weight</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[
          { length: 183, camber: 'minus 7 cm of Balance point', full: '60 kg', half: '30 kg' },
          { length: 188, camber: 'minus 7 cm of Balance point', full: '70 kg', half: '35 kg' },
          { length: 193, camber: 'minus 7 cm of Balance point', full: '80 kg', half: '40 kg' },
        ].map((row) => (
          <TableRow key={row.length}>
            <TableCell>{row.length}</TableCell>
            <TableCell>{row.camber}</TableCell>
            <TableCell>{row.full}</TableCell>
            <TableCell>{row.half}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
</Box>

    ]
  };

const brandDataMap = {
  'Fischer': fischerData,
  'Salomon': { tabs: [], content: [] },
  'Atomic': { tabs: [], content: [] },
  'Rossignol': rossignolData,
  'Madshus': { tabs: [], content: [] },
};

export default function SkiInfoSection() {
  const [brand, setBrand] = useState('Fischer');
  const [tabIndex, setTabIndex] = useState(0);

  const currentBrandData = brandDataMap[brand];

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Ski Info</Typography>
        <Select value={brand} onChange={e => { setBrand(e.target.value); setTabIndex(0); }}>
          {BRANDS.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
        </Select>
      </Box>

      {currentBrandData.tabs.length > 0 && (
        <Tabs value={tabIndex} onChange={(_, newIndex) => setTabIndex(newIndex)} sx={{ mb: 2 }}>
          {currentBrandData.tabs.map((label, idx) => <Tab key={idx} label={label} />)}
        </Tabs>
      )}

      <Box>
        {currentBrandData.content[tabIndex] || <Typography>Select a brand to view information.</Typography>}
      </Box>
    </Box>
  );
}